import TagManager from 'react-gtm-module'
import env from 'config/env'
import StateStorage from 'lib/state-storage'

// https://jestjs.io/docs/environment-variables
const isJest = !!process.env.JEST_WORKER_ID

export const isTestEnv = env.NODE_ENV === 'test' || isJest
const isDevelopment = env.NODE_ENV === 'development'

class Tracking {
  static API_HOST = env.API_HOST

  static EVENT_NAME_PAGE_VIEW = 'page-view'

  static EVENT_NAME_VIEW_CONTENT_CATEGORY = 'view-content-category'

  static EVENT_NAME_VIEW_CONTENT_PRODUCT = 'view-content-product'

  static EVENT_NAME_VIEW_CART = 'view-cart'

  static EVENT_NAME_ADD_TO_CART = 'add-to-cart'

  static EVENT_NAME_PURCHASE = 'purchase'

  static EVENT_NAME_SEARCH = 'search'

  static EVENT_NAME_START_CHECKOUT = 'start-checkout'

  static EVENT_NAME_FINISH_SHIPPING_METHOD = 'finish-shipping-method'

  static EVENT_NAME_FINISH_PAYMENT_METHOD = 'finish-payment-method'

  static META_PIXEL_SEGMENT_EVENTS = {
    'Product Added': 'AddToCart',
    'Quick Add to Cart': 'AddToCart',
    'Order Completed': 'Purchase',
    'Product Viewed': 'ViewContent',
    'Products Searched': 'Search',
    'Product Added to Wishlist': 'AddToWishlist',
    'Checkout Started': 'InitiateCheckout',
  }

  static trackPageView(url = window.location.href, data = {}) {
    const dataLayer = {
      ...data,
      event: Tracking.EVENT_NAME_PAGE_VIEW,
      'Page URL': url,
    }

    Tracking.log(`📈 ${Tracking.EVENT_NAME_PAGE_VIEW}`, dataLayer)

    if (!isTestEnv) {
      TagManager.dataLayer({ dataLayer })
    }
  }

  static enableGoogleEmailTracking = () => {
    const user = StateStorage.getAuthUser()
    const bannerPDPACookieConsent = JSON.parse(
      StateStorage.getCookie('bannerPDPACookieConsent'),
    )
    const isEnableGoogleEmailTracking =
      user && bannerPDPACookieConsent?.is_marketing_allowed

    return isEnableGoogleEmailTracking && user
  }

  static isMetaPixelEnabled = () => {
    if (
      env.META_PIXEL_ID.trim().length === 0 ||
      typeof window === 'undefined' ||
      typeof window.fbq !== 'function'
    ) {
      return false
    }

    try {
      const cookieConsent = StateStorage.getCookie('bannerPDPACookieConsent')
      const parsedCookieConsent =
        typeof cookieConsent === 'string'
          ? JSON.parse(cookieConsent)
          : cookieConsent

      return parsedCookieConsent?.is_marketing_allowed === true
    } catch (err) {
      return false
    }
  }

  static parseMetaPixelNumber = (value) => {
    if (value === undefined || value === null || value === '') {
      return undefined
    }

    const parsedValue = Number(value)

    return Number.isFinite(parsedValue) ? parsedValue : undefined
  }

  static normalizeMetaPixelContentIds = (ids) => {
    if (ids === undefined || ids === null || ids === '') return undefined

    const list =
      typeof ids === 'string' && ids.includes(',') ? ids.split(',') : ids

    const normalizedIds = (Array.isArray(list) ? list : [list])
      .map((id) => id?.toString().trim())
      .filter(Boolean)

    return normalizedIds.length > 0 ? normalizedIds : undefined
  }

  static parseMetaPixelContents = (contents) => {
    if (typeof contents !== 'string') {
      return contents
    }

    try {
      return JSON.parse(contents)
    } catch (err) {
      return undefined
    }
  }

  static normalizeMetaPixelContents = (contents) => {
    const parsedContents = Tracking.parseMetaPixelContents(contents)

    if (!Array.isArray(parsedContents)) return undefined

    const normalizedContents = parsedContents
      .map((item) => {
        const id = item?.id ?? item?.id_product ?? item?.product_id
        const itemPrice = Tracking.parseMetaPixelNumber(
          item?.item_price ?? item?.price,
        )

        return {
          id: id?.toString(),
          ...(itemPrice !== undefined && { item_price: itemPrice }),
          quantity: Tracking.parseMetaPixelNumber(item?.quantity) ?? 1,
        }
      })
      .filter((item) => item.id)

    return normalizedContents.length > 0 ? normalizedContents : undefined
  }

  static getMetaPixelNumItems = (contents) =>
    contents?.reduce((total, item) => total + (item.quantity || 0), 0)

  static removeEmptyMetaPixelParams = (params) =>
    Object.keys(params).reduce((acc, key) => {
      const value = params[key]
      const isEmptyArray = Array.isArray(value) && value.length === 0

      if (
        value !== undefined &&
        value !== null &&
        value !== '' &&
        !isEmptyArray
      ) {
        acc[key] = value
      }

      return acc
    }, {})

  static getMetaPixelSegmentEvent = (eventName, data = {}) => {
    const metaPixelEventName = Tracking.META_PIXEL_SEGMENT_EVENTS[eventName]

    if (!metaPixelEventName) {
      return null
    }

    const { currency } = data

    if (metaPixelEventName === 'AddToCart') {
      const contentIds = Tracking.normalizeMetaPixelContentIds(
        data.product_id ?? data.id_product ?? data.id,
      )
      const itemPrice = Tracking.parseMetaPixelNumber(data.price)
      const quantity = Tracking.parseMetaPixelNumber(data.quantity) ?? 1

      return {
        eventName: metaPixelEventName,
        params: Tracking.removeEmptyMetaPixelParams({
          content_ids: contentIds,
          content_name: data.product_name ?? data.name,
          content_type: 'product',
          contents:
            contentIds?.length > 0
              ? Tracking.normalizeMetaPixelContents([
                  {
                    id: contentIds[0],
                    item_price: itemPrice,
                    quantity,
                  },
                ])
              : undefined,
          currency,
          value:
            currency && itemPrice !== undefined
              ? itemPrice * quantity
              : undefined,
        }),
      }
    }

    const products = Array.isArray(data.products) ? data.products : []
    // Checkout Started reports its line items as cart_contents rather than
    // products, and its total as cart_subtotal. Without these the
    // InitiateCheckout event fires with no contents and no value.
    const cartContents = Array.isArray(data.cart_contents)
      ? data.cart_contents
      : []
    const singleProductId = data.product_id ?? data.id_product
    const contents =
      Tracking.normalizeMetaPixelContents(data.fb_content) ||
      Tracking.normalizeMetaPixelContents(products) ||
      Tracking.normalizeMetaPixelContents(cartContents) ||
      // Product Viewed / Product Added to Wishlist carry one product inline.
      Tracking.normalizeMetaPixelContents(
        singleProductId
          ? [
              {
                id: singleProductId,
                price: data.price,
                quantity: data.quantity ?? 1,
              },
            ]
          : undefined,
      )
    const value =
      Tracking.parseMetaPixelNumber(data.total) ??
      Tracking.parseMetaPixelNumber(data.revenue) ??
      Tracking.parseMetaPixelNumber(data.cart_subtotal) ??
      Tracking.parseMetaPixelNumber(data.price)

    return {
      eventName: metaPixelEventName,
      // Lets Meta dedupe this browser-side Pixel event against the
      // equivalent server-side Conversions API event RudderStack sends for
      // the same order, instead of counting them as two separate events.
      // Must match whatever RudderStack's Facebook CAPI destination uses as
      // its event ID for the same order.
      eventID: data.order_id ? String(data.order_id) : undefined,
      params: Tracking.removeEmptyMetaPixelParams({
        content_ids: Tracking.normalizeMetaPixelContentIds(
          data.product_ids ||
            data.concat_product_ids ||
            data.search_results ||
            // Product Viewed and Product Added to Wishlist describe a single
            // product rather than a list.
            data.product_id ||
            data.id_product ||
            (products.length > 0
              ? products.map((product) => product.id ?? product.id_product)
              : cartContents.map((item) => item.id_product)),
        ),
        content_type: 'product',
        contents,
        currency,
        num_items: Tracking.getMetaPixelNumItems(contents),
        order_id: data.order_id,
        // Meta's Search event is built around the query text; without it the
        // event fires but cannot feed a search-term audience.
        search_string:
          metaPixelEventName === 'Search' ? data.query || undefined : undefined,
        value: currency && value !== undefined ? value : undefined,
      }),
    }
  }

  static trackMetaPixelSegmentEvent(eventName, data) {
    const metaPixelEvent = Tracking.getMetaPixelSegmentEvent(eventName, data)

    if (!metaPixelEvent || !Tracking.isMetaPixelEnabled()) {
      return
    }

    if (metaPixelEvent.eventID) {
      window.fbq('track', metaPixelEvent.eventName, metaPixelEvent.params, {
        eventID: metaPixelEvent.eventID,
      })
      return
    }

    window.fbq('track', metaPixelEvent.eventName, metaPixelEvent.params)
  }

  // Same events and payload as Meta, in GA4 ecommerce format for GTM, under
  // the standard GA4 event names. Purchase is the exception and is pushed as
  // `ga4_purchase`: the Google Ads conversion tags in GTM-KVR77N4R and
  // GTM-T3GDW5P fire on `purchase` and read the legacy trackEvent payload
  // (transactionTotal, purchase-order-id), so a second `purchase` event would
  // corrupt or double-count them. A GTM GA4 event tag triggered on
  // `ga4_purchase` should send it to GA4 as `purchase`.
  static GOOGLE_TAG_MANAGER_SEGMENT_EVENTS = {
    AddToCart: 'add_to_cart',
    ViewContent: 'view_item',
    Search: 'search',
    AddToWishlist: 'add_to_wishlist',
    InitiateCheckout: 'begin_checkout',
    Purchase: 'ga4_purchase',
  }

  // GTM-KVR77N4R is loaded by _document.js whenever marketing is allowed, so
  // consent is the only gate; GOOGLE_TAG_MANAGER_ID is a separate container.
  static isGoogleTagManagerEnabled = () => {
    if (typeof window === 'undefined') {
      return false
    }

    try {
      const cookieConsent = StateStorage.getCookie('bannerPDPACookieConsent')
      const parsedCookieConsent =
        typeof cookieConsent === 'string'
          ? JSON.parse(cookieConsent)
          : cookieConsent

      return parsedCookieConsent?.is_marketing_allowed === true
    } catch (err) {
      return false
    }
  }

  static getGoogleTagManagerSegmentEvent = (eventName, data = {}) => {
    const metaPixelEvent = Tracking.getMetaPixelSegmentEvent(eventName, data)
    const googleEventName =
      Tracking.GOOGLE_TAG_MANAGER_SEGMENT_EVENTS[metaPixelEvent?.eventName]

    if (!googleEventName) {
      return null
    }

    const { params } = metaPixelEvent

    if (googleEventName === 'search') {
      return {
        event: googleEventName,
        ...(params.search_string && { search_term: params.search_string }),
      }
    }

    const items = (params.contents || []).map((item) => ({
      item_id: item.id,
      ...(params.content_name &&
        params.contents.length === 1 && { item_name: params.content_name }),
      ...(item.item_price !== undefined && { price: item.item_price }),
      quantity: item.quantity,
    }))

    return {
      event: googleEventName,
      ecommerce: Tracking.removeEmptyMetaPixelParams({
        // GA4 dedupes purchases on transaction_id.
        transaction_id: params.order_id ? String(params.order_id) : undefined,
        currency: params.currency,
        value: params.value,
        items,
      }),
    }
  }

  static trackGoogleTagManagerSegmentEvent(eventName, data) {
    if (!eventName || !Tracking.isGoogleTagManagerEnabled()) {
      return
    }

    // Every Segment event goes to GTM under its original name, with its
    // properties nested so they cannot collide with GTM/GA keys. Triggers and
    // tags are mapped inside GTM.
    const rawEvent = { event: eventName, properties: { ...data } }

    Tracking.log(`📈 ${eventName}`, rawEvent)
    TagManager.dataLayer({ dataLayer: rawEvent })

    const googleEvent = Tracking.getGoogleTagManagerSegmentEvent(
      eventName,
      data,
    )

    if (!googleEvent) {
      return
    }

    Tracking.log(`📈 ${googleEvent.event}`, googleEvent)

    // GA4 recommends clearing the previous ecommerce object so it does not
    // merge into this event.
    TagManager.dataLayer({ dataLayer: { ecommerce: null } })
    TagManager.dataLayer({ dataLayer: googleEvent })
  }

  static trackEvent(event, data) {
    let dataLayerData = { ...data, event }

    if (
      [Tracking.EVENT_NAME_ADD_TO_CART, Tracking.EVENT_NAME_PURCHASE].includes(
        event,
      )
    ) {
      const enableGoogleEmailTrackingObj = Tracking.enableGoogleEmailTracking()

      dataLayerData = {
        ...data,
        ...(!!enableGoogleEmailTrackingObj && {
          email: enableGoogleEmailTrackingObj.email,
        }),
        event,
      }
    }

    Tracking.log(`📈 ${event}`, dataLayerData)

    if (!isTestEnv) {
      TagManager.dataLayer({ dataLayer: dataLayerData })
    }
  }

  static log(...args) {
    if (window && window.console && isDevelopment && !isTestEnv) {
      console.info(...args) // eslint-disable-line no-console
    }
  }
}

export default Tracking
